import { useEffect, useMemo, useState } from 'react';
import { Search, ArrowRight } from 'lucide-react';
import { Modal } from '@vd/ui';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { useAuth } from '../auth/AuthProvider';
import styles from './CommandPalette.module.css';
import { useGuardedNavigate } from '../../app/NavigationGuard';
import { useTheme } from '../../app/theme';

interface Action {
  id: string;
  label: string;
  hint?: string;
  onSelect: () => void;
}

export interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
}

export function CommandPalette({ open, onClose }: CommandPaletteProps) {
  const navigate = useGuardedNavigate();
  const { currentCompanyId } = useAuth();
  const { toggle: toggleTheme } = useTheme();
  const [q, setQ] = useState('');
  const [index, setIndex] = useState(0);

  const { data: projects = [] } = useQuery({
    queryKey: ['projects', currentCompanyId],
    queryFn: () => currentCompanyId ? api.listProjects(currentCompanyId) : Promise.resolve([]),
    enabled: open && !!currentCompanyId,
  });

  const actions: Action[] = useMemo(() => [
    ...projects.map<Action>((p) => ({
      id: `p-${p.id}`,
      label: `Go to ${p.name}`,
      hint: 'Project',
      onSelect: () => { navigate(`/projects/${p.id}/board`); onClose(); },
    })),
    { id: 'mcp', label: 'Open MCP setup', onSelect: () => { navigate('/me/settings/mcp-setup'); onClose(); } },
    { id: 'company', label: 'Company settings', onSelect: () => { navigate('/company/settings'); onClose(); } },
    { id: 'user', label: 'Your settings', onSelect: () => { navigate('/me/settings'); onClose(); } },
    // Through the provider, not straight at the DOM. Writing `data-theme`
    // and localStorage here left the provider's own state stale (so the next
    // toggle from the top bar flipped back to where it thought it was) and
    // never reached the account, which is where the setting now lives.
    { id: 'theme', label: 'Toggle theme', onSelect: () => { toggleTheme(); onClose(); } },
  ], [projects, navigate, onClose, toggleTheme]);

  const filtered = useMemo(() => {
    const q2 = q.trim().toLowerCase();
    if (!q2) return actions;
    return actions.filter((a) => a.label.toLowerCase().includes(q2));
  }, [q, actions]);

  useEffect(() => { setIndex(0); }, [q]);
  useEffect(() => { if (open) { setQ(''); setIndex(0); } }, [open]);

  return (
    <Modal open={open} onClose={onClose} width={520}>
      <div className={styles.palette}>
        <div className={styles.inputRow}>
          <Search size={16} style={{ color: 'var(--text-tertiary)' }} />
          <input
            autoFocus
            className={styles.input}
            placeholder="Jump to project, action, or ticket…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown') { e.preventDefault(); setIndex((i) => Math.min(filtered.length - 1, i + 1)); }
              if (e.key === 'ArrowUp')   { e.preventDefault(); setIndex((i) => Math.max(0, i - 1)); }
              if (e.key === 'Enter')     { filtered[index]?.onSelect(); }
            }}
          />
        </div>
        <div className={styles.list}>
          {filtered.length === 0 && <div className={styles.empty}>No matches</div>}
          {filtered.map((a, i) => (
            <div
              key={a.id}
              className={i === index ? `${styles.row} ${styles.rowActive}` : styles.row}
              onMouseEnter={() => setIndex(i)}
              onClick={() => a.onSelect()}
            >
              <span>{a.label}</span>
              {a.hint && <span className={styles.hint}>{a.hint}</span>}
              {i === index && <ArrowRight size={12} style={{ color: 'var(--text-tertiary)' }} />}
            </div>
          ))}
        </div>
      </div>
    </Modal>
  );
}
